import { computed, inject, type Ref, watch, unref, type MaybeRef } from "vue"
import { ModelController } from "@/controllers/model-controllers/abstract/model-controller"
import useVuelidate from "@vuelidate/core"
import type { BaseModel } from "@/models/base.model"

type FormDataInjectorConfig = {
  controller?: ModelController<any, any, any>
  createHandler?: () => Promise<void>
  updateHandler?: () => Promise<void>
  customSaveHandler?: (save: () => Promise<void>) => Promise<void>
  rules?: MaybeRef<{ [key: string]: object }>
  creationRules?: MaybeRef<{ [key: string]: object }> // Optional to allow for different rules for creation
  updateRules?: MaybeRef<{ [key: string]: object }> // Optional to allow for different rules for update
  autoClose?: boolean
}

export function useFormConfigurator<ModelType extends object>(config: FormDataInjectorConfig = {}) {
  const model: Ref<ModelType> = inject("model")!
  const initialText: string | undefined = inject("initialText")
  const isCreating: Ref<boolean> = inject("isCreating")!
  const autoClose: Ref<boolean> = inject("autoClose")!

  if (config.autoClose !== undefined) {
    autoClose.value = config.autoClose
  }

  const closeForm: () => void = inject("closeForm")!
  const saveHandle: (model: BaseModel) => void = inject("saveHandle")!

  // Variables that can be set for the wrapper
  const _controller: Ref<ModelController<any, any, any> | undefined> = inject("controller")!
  const _createHandler: Ref<(() => Promise<void>) | undefined> = inject("createHandler")!
  const _updateHandler: Ref<(() => Promise<void>) | undefined> = inject("updateHandler")!
  const _customSaveHandler: Ref<((save: () => Promise<void>) => Promise<void>) | undefined> =
    inject("customSaveHandler")!
  const _isFormInvalid: Ref<boolean> = inject("isFormInvalid")!

  _controller.value = config.controller
  _createHandler.value = config.createHandler
  _updateHandler.value = config.updateHandler
  _customSaveHandler.value = config.customSaveHandler

  const rules = computed(
    () =>
      (isCreating.value ? unref(config.creationRules) : unref(config.updateRules)) ??
      unref(config.rules) ??
      {}
  )
  const v$ = useVuelidate(rules, model.value)

  const isFormInvalid = computed(() => {
    return v$.value.$invalid
  })
  watch(
    isFormInvalid,
    (value) => {
      _isFormInvalid.value = value
    },
    { immediate: true }
  )

  return {
    model,
    initialText,
    isCreating,
    v$,
    closeForm,
    saveHandle
  }
}
